/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import StatusLight from '@react/react-spectrum/StatusLight';
import Heading from '@react/react-spectrum/Heading';
import Link from '@react/react-spectrum/Link';
import ChevronRight from '@react/react-spectrum/Icon/ChevronRight';
import ChevronDown from '@react/react-spectrum/Icon/ChevronDown';
import classnames from 'classnames';
import { Card } from '@react/react-spectrum/Card';
import uuid from 'uuid';
import { interpretKey } from '../../../../utils/eventUtil';
import RightTileSection from './rightSection';
import LeftTileSection from './leftSection';
import TranslationUtil from '../../../../utils/translation';
import { ACTION_ID } from '../../../../utils/modelMapper/appModelMapper/appModelConstants';
import { appTileAnalytics } from '../../../../analytics';
import { ANALYTICS } from '../../../../utils/enums';
import './index.scss';

const maxDescLength = 850; // px

const getContentLength = (text) => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.fontWeight = 400;
    div.style.fontSize = '14px';
    div.innerHTML = text;
    document.body.appendChild(div);
    const width = div.offsetWidth;
    document.body.removeChild(div);
    return (width);
};

const getDescLengthToRender = (text) => {
    let minLength = 0;
    let maxLength = text.length - 1;
    while (minLength < maxLength) {
        const mid = Math.floor((minLength + maxLength + 1) / 2);
        const contentLength = getContentLength(text.substr(0, mid));
        if (contentLength === maxDescLength) {
            return mid;
        } if (contentLength > maxDescLength) {
            maxLength = mid - 1;
        } else {
            minLength = mid;
        }
    }
    return minLength;
};

const onLabelClick = (app, action = {}, appSectionFilter) => {
    app.onAppAction(action.actionId, app);
    appTileAnalytics(ANALYTICS.EVENTS.MORE_INFO_CLICK, {
        app,
        appSectionFilter,
        actionId: action.actionId,
    });
};

class AppCard extends Component {
    onSeeMoreClick = (e, actionId, app, appSectionBasedFields, appSectionFilter) => {
        const { onAppAction } = app;
        const {
            localizedUpdateDescription = [],
            seeMoreLinkUrl,
        } = appSectionBasedFields;
        e.stopPropagation();
        const guid = uuid.v4();
        onAppAction(actionId, app, { localizedUpdateDescription, seeMoreLinkUrl, guid });
        appTileAnalytics(ANALYTICS.EVENTS.SEE_MORE_CLICK, {
            actionId,
            app,
            appSectionFilter,
            guid,
	        seeMoreLinkUrl,
        });
    }

    shouldShowStatusLabel(appSectionBasedField) {
        const {
            showStatusLabel, showSessionErrorOnly, statusLabelList,
        } = appSectionBasedField;
        if (!showStatusLabel) {
            return false;
        }
        // for those sections where only session error should be shown
        if (showSessionErrorOnly) {
            return statusLabelList.some(label => (label && label.errorType === 'sessionError'));
        }
        return true;
    }

    renderVersionsDropDown() {
        const { versionsDropDownCallback, isDropDownCollapsed } = this.props;

        return (
            <div
                className="drop-down-icon"
                onClick={versionsDropDownCallback}>
                {isDropDownCollapsed ? <ChevronRight size="L" /> : <ChevronDown size="L" />}
            </div>
        );
    }

    renderTagline(localizedTagline, progress) {
        if (!localizedTagline) {
            return [];
        }
        return (
            <Heading
                className={`listView-appTile-tagline ${progress ? 'hide-during-progress' : ''}`}
                variant="subtitle3">
                {localizedTagline}
            </Heading>
        );
    }


    renderLabel({
        app, appSectionFilter, progress, statusLabelList = [],
    }) {
        const statusLabels = statusLabelList.map(({ variant, action, localizedText }) => (
            <StatusLight
                variant={variant}
                aria-label={localizedText}
                className={`listView-appTile-label ${progress ? 'hide-during-progress' : ''}`}
            >
                <Link
                    className={classnames('status-info', {
                        nonClickable: !action,
                    })}
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (action && action.actionId) {
                            onLabelClick(app, action,
                                appSectionFilter);
                        }
                    }}
                    onKeyDown={interpretKey({
                        onEnter: [
                            (e) => {
                                e.stopPropagation();
                                if (action && action.actionId) {
                                    onLabelClick(app, action,
                                        appSectionFilter);
                                }
                            }, true,
                        ],
                    })}
                >
                    {localizedText}
                </Link>
            </StatusLight>
        ));
        return (
            <div className="labels-wrapper">
                {statusLabels}
            </div>
        );
    }

    renderUpdateDescription = ({
        appSectionBasedFields, app, appSectionFilter, progress,
    }) => {
        const {
            seeMoreLink = {},
            localizedUpdateDescription = [],
            seeMoreLinkUrl,
        } = appSectionBasedFields;
        const { actionId } = seeMoreLink;

        const descriptionLengthToRender = getDescLengthToRender(localizedUpdateDescription[0]);
        const updateDescriptionDisplayed = localizedUpdateDescription[0]
            .substr(0, descriptionLengthToRender + 1);

        const isContentOverflowing = localizedUpdateDescription[0].length
            > (descriptionLengthToRender + 1);
        const showSeeMore = seeMoreLinkUrl
            || localizedUpdateDescription.length > 1 || isContentOverflowing;

        return (
            <div className={`listView-appTile-update-description ${progress ? 'hide-during-progress' : ''}`}>
                <div
                    key={0}
                    className="update-description-text"
                >
                    {`${updateDescriptionDisplayed} ${isContentOverflowing ? '...' : ''} `}
                    {showSeeMore
                        && (
                            <Link
                                key={1}
                                id="update-text-see-more"
                                className={
                                    classnames('listView-appTile-update-text-view-more', {
                                        show: app.isPatchUpdate()
                                            || (seeMoreLinkUrl && app.isFullAppUpdate()),
                                    })}
                                onKeyDown={interpretKey({
                                    onEnter: [
                                        (e) => {
                                            e.stopPropagation();
                                            this.onSeeMoreClick(
                                                e, actionId, app, appSectionBasedFields,
                                                appSectionFilter
                                            );
                                        }, true,
                                    ],
                                })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.onSeeMoreClick(
                                        e, actionId, app, appSectionBasedFields, appSectionFilter
                                    );
                                }}>
                                {TranslationUtil.getMessage('ccx.update.description.viewMore')}
                            </Link>
                        )
                    }

                </div>
            </div>
        );
    }

    render() {
        const {
            app, appSectionFilter, className, sectionHeader, appSection,
            showVersionsDropDown, showExtraMargin, subSectionFilter, layoutId,
        } = this.props;
        const selectedField = app.appSectionBasedFields.find((
            { appSectionFilter: appSectionFilterArray }
        ) => appSectionFilterArray.includes(appSectionFilter));
        const { onAppAction, actionIds } = app;

        if (!selectedField) {
            return [];
        }
        const { isCardClickable, isCategorySection } = selectedField;
        const cardContextId = `${String(app.sapcode).toUpperCase()}_${app.codexVersion}`;
        const showLabel = this.shouldShowStatusLabel(selectedField);
        return (
            <Card
                className={`${className} ${isCardClickable || !app.isAppInstalled() ? 'clickable-app-tile' : ''}`}
                allowsSelection={false}
                tabIndex={0}
                onMouseDown={(e) => { e.preventDefault(); }}
                onClick={() => {
                    if (isCardClickable || !app.isAppInstalled()) {
                        onAppAction(ACTION_ID.APP_TILE_CLICK, app, { isCategorySection });
                        appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
                            app,
                            appSectionFilter,
                            actionId: actionIds.select,
                            viewType: ANALYTICS.VIEW_TYPE[layoutId],
                        });
                    }
                }}
                onKeyDown={interpretKey({
                    onEnter: [() => {
                        if (isCardClickable || !app.isAppInstalled()) {
                            onAppAction(actionIds.select, app, { isCategorySection });
                            appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
                                app,
                                appSectionFilter,
                                actionId: actionIds.select,
                                viewType: ANALYTICS.VIEW_TYPE[layoutId],
                            });
                        }
                    },
                    true,
                    ],
                })}>
                <div
                    className={classnames(`listView_appTile_container ${cardContextId}`, {
                        clickable: (isCardClickable
                        || !app.isAppInstalled()),
                    })}
                    render-guid={cardContextId}
                >
                    {showVersionsDropDown && this.renderVersionsDropDown()}
                    {showExtraMargin && <span className="spacer-div">&nbsp;&nbsp;</span>}
                    <LeftTileSection
                        app={app}
                        appSectionBasedFields={selectedField}
                        appSectionFilter={appSectionFilter}
                        subSectionFilter={subSectionFilter}
                        sectionHeader={sectionHeader}
                        renderUpdateDescription={this.renderUpdateDescription}
                        renderTagline={this.renderTagline}
                        showLabel={showLabel}
                        appSection={appSection}
                        layoutId={layoutId} />
                    <RightTileSection
                        app={app}
                        appSectionBasedFields={selectedField}
                        appSectionFilter={appSectionFilter}
                        renderLabel={this.renderLabel}
                        showLabel={showLabel}
                        sectionHeader={sectionHeader}
                        layoutId={layoutId} />
                </div>
            </Card>
        );
    }
}

export default observer(AppCard);
